import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import styled from "styled-components";
import { useExtensionContext } from "../ExtensionContext";
import SelectableTaskCard from "app/popleads/modules/extension/components/SelectableTaskCard";
import EmptyStatus from "app/popleads/components/ui-kit/EmptyStatus";
import Spinner from "app/popleads/components/ui-kit/Spinner";
import Modal from "app/popleads/components/ui-kit/Modal";
import useTaskDelete from "app/popleads/hooks/useTaskDelete";
import { toast } from "react-toastify";
import moment from "moment";
import { useSelector } from "react-redux";

const ExtensionPopupContentBody = styled.div`
    width: 100%;
    height: 100%;
    padding: 12px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
`;

const LoadMoreContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    min-height: 60px;
`;

const StyledTaskContainer = styled.div`
    margin-bottom: 12px;
    position: relative;
    //z-index: ${(props) => (props.isDatePickerOpen || props.isExpanded ? 100 : 1)};

    &:last-child {
        margin-bottom: 20px;
    }
`;

const ExtensionProfileTasksPage = () => {
    const {
        profile,
        isProfileLoading,
        modals,
        selectedModal,
        setState,
        refetchTaskList,
        modalMessage,
    } = useExtensionContext();
    const [selectedTasks, setSelectedTasks] = useState([]);
    const [taskToDelete, setTaskToDelete] = useState(null);
    const [displayedTasks, setDisplayedTasks] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [expandedTaskIds, setExpandedTaskIds] = useState([]);
    const [datePickerTaskId, setDatePickerTaskId] = useState(null);
    const { deleteTask } = useTaskDelete();
    const loadMoreRef = useRef(null);
    const observerRef = useRef(null);

    const { user } = useSelector((state) => state.auth);

    const hasTaskPermission = (task) => {
        if (task.kullaniciId) {
            return task.kullaniciId && task.kullaniciId === user?.id;
        }
        return true;
    };

    const handleNoPermission = () => {
        toast.error("You do not have permission to edit this task");
    };

    const ITEMS_PER_PAGE = 20;
    const allTasks = profile?.gorev || [];

    // Sort tasks by date and time proximity to today
    const sortedTasks = useMemo(() => {
        if (allTasks.length === 0) return [];

        const today = new Date();
        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        return [...allTasks].sort((a, b) => {
            const dateA = new Date(a.zaman);
            const dateB = new Date(b.zaman);

            const dateAOnly = new Date(dateA.getFullYear(), dateA.getMonth(), dateA.getDate());
            const dateBOnly = new Date(dateB.getFullYear(), dateB.getMonth(), dateB.getDate());

            const diffA = Math.abs(dateAOnly - todayOnly);
            const diffB = Math.abs(dateBOnly - todayOnly);

            if (diffA !== diffB) {
                return diffA - diffB;
            }
            return dateA - dateB;
        });
    }, [allTasks]);

    // Load more tasks
    const loadMoreTasks = useCallback(() => {
        if (isLoadingMore || !hasMore) return;

        setIsLoadingMore(true);

        setTimeout(() => {
            const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
            const endIndex = startIndex + ITEMS_PER_PAGE;
            const newTasks = sortedTasks.slice(startIndex, endIndex);

            if (newTasks.length === 0) {
                setHasMore(false);
            } else {
                setDisplayedTasks((prev) => [...prev, ...newTasks]);
                setCurrentPage((prev) => prev + 1);

                if (endIndex >= sortedTasks.length) {
                    setHasMore(false);
                }
            }

            setIsLoadingMore(false);
        }, 300);
    }, [currentPage, sortedTasks, isLoadingMore, hasMore]);

    useEffect(() => {
        setCurrentPage(1);
        setHasMore(true);
        setDisplayedTasks([]);

        if (sortedTasks.length > 0) {
            const initialTasks = sortedTasks.slice(0, ITEMS_PER_PAGE);
            setDisplayedTasks(initialTasks);
            setCurrentPage(2);
            setHasMore(sortedTasks.length > ITEMS_PER_PAGE);
        }
    }, [sortedTasks]);

    useEffect(() => {
        if (!loadMoreRef.current) return;

        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
                    loadMoreTasks();
                }
            },
            { threshold: 0.1 }
        );

        observerRef.current.observe(loadMoreRef.current);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [loadMoreTasks, hasMore, isLoadingMore]);

    const handleTaskComplete = (taskId) => {
        if (taskId === null) {
            setSelectedTasks((prev) => prev.filter((id) => id !== taskId));
        } else {
            setSelectedTasks((prev) => [...prev, taskId]);
        }
    };

    const handleTaskDelete = async (id) => {
        const task = allTasks.find((t) => t.id === id);
        if (task && !hasTaskPermission(task)) {
            handleNoPermission();
            return;
        }

        try {
            await deleteTask(id);
            //refetchProfile();
            await refetchTaskList();
            toast.success("Task successfully deleted");
        } catch (error) {
            toast.error("Error deleting task");
        }
    };

    if (isProfileLoading) {
        return (
            <ExtensionPopupContentBody>
                <LoadingContainer>
                    <Spinner size={32} thickness={4} variant="l3rnTech" />
                </LoadingContainer>
            </ExtensionPopupContentBody>
        );
    }

    return (
        <>
            <ExtensionPopupContentBody>
                {displayedTasks.length > 0 ? (
                    <>
                        {displayedTasks.map((task, index) => (
                            <StyledTaskContainer
                                key={task.id}
                                isExpanded={expandedTaskIds.includes(task.id)}
                                isDatePickerOpen={datePickerTaskId === task.id}
                                index={index}
                            >
                                <SelectableTaskCard
                                    task={task}
                                    setTaskToDelete={setTaskToDelete}
                                    overdue={moment(task.zaman).isBefore(moment())}
                                    selectedTasks={selectedTasks}
                                    onTaskComplete={handleTaskComplete}
                                    hasTaskPermission={hasTaskPermission}
                                    handleNoPermission={handleNoPermission}
                                    isProfileTask={true}
                                    isProfileTaskFirst={true}
                                    index={index}
                                    showInlineInfo={false}
                                    isExpanded={expandedTaskIds.includes(task.id)}
                                    onToggleExpand={() =>
                                        setExpandedTaskIds((prev) =>
                                            prev.includes(task.id)
                                                ? prev.filter((id) => id !== task.id)
                                                : [...prev, task.id]
                                        )
                                    }
                                    onDatePickerToggle={(isOpen) => setDatePickerTaskId(isOpen ? task.id : null)}
                                />
                            </StyledTaskContainer>
                        ))}

                        {hasMore && (
                            <LoadMoreContainer ref={loadMoreRef}>
                                {isLoadingMore ? (
                                    <Spinner size={16} thickness={3} variant="l3rnTech" />
                                ) : (
                                    <div>Loading more tasks...</div>
                                )}
                            </LoadMoreContainer>
                        )}
                    </>
                ) : (
                    <EmptyStatus description="No tasks found for this profile." />
                )}
            </ExtensionPopupContentBody>

            <Modal
                isOpen={selectedModal === modals.DELETE_PROFILE_TASK}
                successButtonText="Yes, Delete"
                cancelButtonText="Not Now"
                onClose={() =>
                    setState((prevState) => ({
                        ...prevState,
                        selectedModal: null,
                    }))
                }
                title={modalMessage ? modalMessage : "Are you sure you want to delete this task?"}
                variant="danger"
                icon="delete"
                onSuccess={() => {
                    handleTaskDelete(taskToDelete);
                    setState((prevState) => ({
                        ...prevState,
                        selectedModal: null,
                    }));
                }}
            >
                <div>This action cannot be undone.</div>
            </Modal>
        </>
    );
};

export default ExtensionProfileTasksPage;
